import { useQuery } from '@tanstack/react-query';
import { queryConfigs } from '@/providers/tanstack/query-client';
import { featureFlagsApiService } from '@/services/feature-flags.service';

import { ActiveFlagsMap } from '@/types/entities/feature-flag';

// Shared query key — all instances resolve to the same cached request.
// Exported so mutation handlers can invalidate after toggle / create / update / delete.
export const ACTIVE_FLAGS_QUERY_KEY = ['featureFlags', 'active'];

// Feature-flag queries use referenceData staleTime (30 min) to avoid unnecessary
// refetches while the sidebar stays mounted, but override cacheTime to 0 so the
// cache is discarded as soon as all observers unmount.  This guarantees that when
// the user navigates from SettingLayout (where flags are toggled) back to
// DashboardLayout (where the sidebar mounts), there is no stale cached entry —
// a fresh request is always made.
const featureFlagQueryConfig = {
  ...queryConfigs.referenceData,
  cacheTime: 0,
};

/**
 * Returns the state of a single feature flag including loading status.
 * All instances share one HTTP request via the shared query key.
 *
 * @example
 * const { enabled, isLoading } = useFeatureFlag('ODOO_ENABLED_INTEGRATION');
 * <ShowIf condition={enabled}><OdooSection /></ShowIf>
 */
export function useFeatureFlag(flagKey: string): { enabled: boolean; isLoading: boolean } {
  const { data, isLoading } = useQuery<ActiveFlagsMap>(
    ACTIVE_FLAGS_QUERY_KEY,
    () => featureFlagsApiService.getActive(),
    featureFlagQueryConfig
  );

  return { enabled: data?.[flagKey] ?? false, isLoading };
}

/**
 * Returns the full active flags map.
 * Intended for admin use (e.g. feature-flag-list-view).
 */
export function useFeatureFlags(): ActiveFlagsMap {
  const { data } = useQuery<ActiveFlagsMap>(
    ACTIVE_FLAGS_QUERY_KEY,
    () => featureFlagsApiService.getActive(),
    featureFlagQueryConfig
  );

  return data ?? {};
}
